
import { useIsAuthenticated } from "@azure/msal-react";
import axios, { AxiosInstance, AxiosProgressEvent } from "axios";

/**
 * A client for interacting with the Cosme Widget API.
 */
class CosmeWidgetAPIClient {

    async deleteMaster(id: string): Promise<Master | void> {
        return await this.axios.delete("master/delete", {
            data: {
                "id": id
            }
        }).then((res) => {
        });
    };

    async uploadMaster(

        uploadType: string,
        file: File,
        progressCallback: (event: AxiosProgressEvent) => void,
    ): Promise<Job> {
        return await this.axios.post("jobs/upload/master", {
            "author": this.author,
            "uploadType": uploadType,
            "uploadFile": file,
        }, {
            onUploadProgress: progressCallback,
            headers: {
                'Content-Type': 'multipart/form-data',
                'Access-Controll-Allow-Origin': '*', // Required for CORS support to work
            }
        }).then((res) => { return res.data });
    }

    async downloadFile(path: string | undefined): Promise<DownloadLink> {
        return await this.axios.get("jobs/download", {
            params: {
                path: path
            },
        })

            .then((res) => {
                return res.data;
            });
    }
    nop = () => {
        return;
    };
    // ===================================
    // Job
    // ===================================
    /**
     * Creates a new job of the specified type.
     * @param jobType The type of job to create.
     * @returns A Promise that resolves to the created job.
     */
    async createDownloadJob(): Promise<Job> {
        return await this.axios.post("jobs/", {
            "author": this.author,
            "type": "MASTER_DOWNLOAD",
        }).then((res) => {
            return res.data;
        });
    }


    /**
     * Retrieves a list of jobs of the specified type.
     * @param jobType The type of job to retrieve.
     * @returns A Promise that resolves to an array of jobs.
     */
    async jobs(jobType?: string[], status?: string[]): Promise<Job[]> {
        return await this.axios.get("jobs/", {
            params: {
                "type": jobType,
                "status": status,
            }
        }).then((res) => {
            return res.data;
        });
    }

    importMasterJob = async (): Promise<Job[]> => {
        return await this.axios.post("jobs/import/master", {}).then((res) => { return res.data });
    };

    async downloadMasterJob(status?: string[]): Promise<Job[]> {
        return await this.axios.get("jobs/", {
            params: {
                "type": "MASTER_DOWNLOAD",
                "status": status,
                "limit": 5,
                "sort": "_ts",
                "order": "DESC",
                "offset": 0,
            }
        }).then((res) => {
            return res.data;
        });
    }



    // ===================================
    // Statistics
    // ===================================
    async getArchiveStatistics(): Promise<SimpleStatistics[]> {
        return await this.axios.get("stat/archive").then((res) => {
            return res.data;
        });
    }

    async getQueueCounts(): Promise<SimpleStatistics[]> {
        return await this.axios.get("stat/queue").then((res) => {
            return res.data;
        });
    }


    // ===================================
    // Master
    // ===================================
    async addMaster(
        maker: string,
        brandYomi: string,
        brand: string,
        item: string,
        color: string | undefined,
        productCategory: string | undefined,
        itemCategory: string | undefined,
    ) {
        return await this.axios.post("master/add", {
            maker: maker,
            brandYomi: brandYomi,
            brand: brand,
            item: item,
            color: color,
            productCategory: productCategory,
            itemCategory: itemCategory,
        }).then((res) => {
            return res.data;
        });
    }

    async pickupMaster(maker: string | undefined, brand: string | undefined, item: string | undefined, color: string | undefined) {
        return await this.axios.get("master/pickup", {
            params: {
                maker: maker,
                brand: brand,
                item: item,
                color: color
            }
        }).then((res) => {
            return res.data;
        });
    }

    async searchMaster(words: string, limit: number = 20, offset: number = 0) {
        return await this.axios.get("master/search", {
            params: {
                q: words,
                limit: limit,
                offset: offset,
            }
        }).then((res) => {
            return res.data;
        });
    }

    /**
     * Retrieves a list of suggested keywords for the specified column and keyword.
     * @param column The column to search for suggested keywords.
     * @param keyword The keyword to search for suggestions.
     * @returns A Promise that resolves to an array of strings representing the suggested keywords.
     */
    async suggest(column: string, keyword: string): Promise<string[]> {
        return await this.axios.get("master/suggest", {
            params: {
                column: column,
                keyword: keyword
            }
        }).then((res) => {
            return res.data;
        });
    }
    /**
   * Retrieves a list of Master objects that match the specified search criteria.
   * @param maker The maker to search for.
   * @param brand The brand to search for.
   * @param item The item to search for.
   * @param color The color to search for.
   * @returns A Promise that resolves to an array of Master objects that match the search criteria.
   */
    async getMaster(maker: string, brand: string, item: string, color: string): Promise<Master[]> {
        let params = {
            maker: "",
            brand: "",
            item: "",
            color: "",
        };
        if (maker !== undefined && maker !== "") {
            params['maker'] = maker;
        }
        if (brand !== undefined && brand !== "") {
            params['brand'] = brand;
        }
        if (item !== undefined && item !== "") {
            params['item'] = item;
        }
        if (color !== undefined && color !== "") {
            params['color'] = color;
        }
        return await this.axios.get("master/pickup", {
            params: params,
        }).then((res) => {
            return res.data;
        });
    }

    // ===================================
    // Archive
    // ===================================
    /**
     * Searches for extracts based on the given search parameters.
     * @param limit The maximum number of results to return.
     * @param status The status of the extracts to search for.
     * @param offset The offset to start returning results from.
     * @param media The media type to search for.
     * @param maker The maker to search for.
     * @param brand The brand to search for.
     * @returns A Promise that resolves to an array of Archive objects that match the search criteria.
     */
    async searchArchives({ limit = 30, status = 'DETECTED', offset = 0, media = undefined, startDate = undefined, endDate = undefined }: SearchRequest): Promise<Archive[]> {
        return await this.axios.post("archive/search", {
            limit: limit,
            offset: offset,
            status: status,
            media: media,
            startDate: startDate,
            endDate: endDate,
        }).then((res) => {
            return res.data;
        })
    }

    async searchCount({ limit = 30, status = 'DETECTED', offset = 0, media = undefined, startDate = undefined, endDate = undefined }: SearchRequest): Promise<Count> {
        return await this.axios.post("archive/search/count", {
            status: status,
            media: media,
            startDate: startDate,
            endDate: endDate,
        }).then((res) => {
            return res.data;
        });
    }


    /**
     * Retrieves the total count of all archives with the specified status.
     * @param status An array of status strings to filter archives by.
     * @returns A Promise that resolves to a Count object containing the total count of archives.
     */
    async getArchiveAllCount(status: string[]): Promise<Count> {
        return await this.axios.post("archive/count/all", {
            status: status,
        }).then((res) => {
            return res.data;
        });
    }
    /**
     * Retrieves the archive with the specified ID.
     * @param archiveId The ID of the archive to retrieve.
     * @returns A Promise that resolves to the Archive object with the specified ID.
     */
    async getArchive(archiveId: string): Promise<ExtendedArchive> {
        return await this.axios.get("archive/", {
            params: {
                archiveId: archiveId
            }
        }).then((res) => {
            return res.data;
        });
    }


    /**
     * Retrieves the MHTML content for the archive with the specified path.
     * @param archivePath The path of the archive to retrieve MHTML content for.
     * @returns A Promise that resolves to a string representing the MHTML content for the archive.
     */
    async getMhtml(archivePath: string): Promise<string> {
        let mhtml = await this.axios.get("archive/mhtml", {
            params: {
                archivePath: archivePath
            }
        }).then((res) => {
            return res.data;
        });
        return mhtml;
    }

    /**
     * Retrieves a temporary URL for the archive with the specified path.
     * @param archivePath The path of the archive to retrieve a temporary URL for.
     * @returns A Promise that resolves to a string representing the temporary URL for the archive.
     */
    async getArchiveTmpUrl(archivePath: string): Promise<string> {
        return await this.axios.get("archive/mhtml", {
            params: {
                archivePath: archivePath
            }

        }).then((res) => {
            return res.data;
        });
    }
    /**
     * Fixes the specified archive.
     * @param url The URL of the archive to fix.
     * @param archivePath The path of the archive to fix.
     * @returns A Promise that resolves to the fixed archive object.
     */
    async fixArchive(url: string, archivePath: string) {
        return await this.axios.post("archive/fix", {
            url: url,
            archivePath: archivePath,
            author: this.author,
        }).then((res) => {
            return res.data[0];
        });
    }
    async pendingArchive(url: string, archivePath: string) {
        return await this.axios.post("archive/pending", {
            url: url,
            archivePath: archivePath,
            author: this.author,
        }).then((res) => {
            return res.data[0];
        });
    }
    /**
     * Ignores the archive with the specified URL and path.
     * @param url The URL of the archive to ignore.
     * @param archivePath The path of the archive to ignore.
     * @returns A Promise that resolves to the ignored archive object.
     */
    async ignoreArchive(url: string, archivePath: string) {
        return await this.axios.post("archive/ignore", {
            url: url,
            archivePath: archivePath,
            author: this.author,
        }).then((res) => {
            return res.data[0];
        });

    }

    // ===================================
    // Extract
    // ===================================

    /**
     * Fixes the specified extract.
     * @param extract The extract to fix.
     * @returns A Promise that resolves to the fixed extract object.
     */
    async fixExtract(extract: Extract) {
        extract.author = this.author;
        return await this.axios.post("extract/fix", {
            extract: extract,
        }).then((res) => {
            return res.data;
        });
    }
    /**
     * Ignores the extract with the specified ID and URL.
     * @param extractId The ID of the extract to ignore.
     * @param url The URL of the extract to ignore.
     * @returns A Promise that resolves to the ignored extract object.
     */
    async ignoreExtract(extractId: string, url: string) {
        return await this.axios.post("extract/ignore", {
            extractId: extractId,
            url: url,
            author: this.author,
        }).then((res) => {
            return res.data;
        });
    }


    /**
     * Retrieves the count of extracts for the specified URLs.
     * @param archiveIds An array of URLs to retrieve the count of extracts for.
     * @returns A Promise that resolves to an array of Count objects containing the count of extracts for each URL.
     */
    async getExtractCount(archiveIds: string[]): Promise<Count[]> {
        return await this.axios.post("extract/count", {
            archiveIds: archiveIds
        }).then((res) => {
            return res.data;
        });

    }

    /**
     * Retrieves all extracts for the archive with the specified ID.
     * @param archiveId The ID of the archive to retrieve extracts for.
     * @returns A Promise that resolves to an array of Extract objects for the specified archive.
     */
    async getExtracts(archiveId: string): Promise<Extract[]> {
        return await this.axios.get("extract/a", {
            params: {
                archiveId: archiveId
            }
        }).then((res) => {
            return res.data;
        });
    }

    async reExtract(archiveId: string): Promise<Extract[]> {
        return await this.axios.post("extract/re", {
            archiveId: archiveId
        }).then((res) => {
            return res.data;
        });
    }

    static convertDataColumn<T>(records: T[]) {
        // return records.map((r) => {
        //     console.log("convert Data COlumn map.", r);
        // });
        // new Date(Date.parse(f.createdAt) + 9 * 60 * 60 * 1000);
    }



    // ===================================
    //   constructor
    // ===================================
    axios: AxiosInstance;
    author: string;
    token: string;
    // constructor() {
    //     this.axios = CosmeWidgetAPIClient.createInstance();
    //     this.author = "";
    // }
    constructor(accessToken: string) {
        this.axios = CosmeWidgetAPIClient.createInstance();
        this.token = accessToken;
        this.author = "";
    }
    setUserName(displayName: string) {
        this.author = displayName;
    }
    static createInstance(): AxiosInstance {
        const instance: AxiosInstance = axios.create({
            baseURL: process.env.REACT_APP_API_SERVER,
            headers: {
                "Content-type": "application/json;charset=utf-8",
                "Accept": "application/json",
                // "Access-Control-Allow-Origin": "api.cosme-widget.com",
                "Access-Control-Allow-Origin": process.env.REACT_APP_ALLOW_ORIGIN,
                "Authorization": "Bearer "
            },
            paramsSerializer: (params) => {
                return Object.entries(params)
                    .map(([key, value]) => {
                        if (Array.isArray(value)) {
                            return value.map((v) => {
                                return `${key}=${v}`;
                            }).join("&");
                        } else {
                            return `${key}=${value}`
                        }
                    }).join("&");
            },
            timeout: 30000,
        });
        return instance;
    }



    getAuthor = () => {
        const isAuthenticated = useIsAuthenticated();
        if (isAuthenticated) {
        }
    }

    convertDateColumn = (records: any[]) => {
        return records.map((r) => {
            console.log("convertDateColumn map.", typeof r);
            Object.keys(r).forEach((key) => {
                if (typeof r[key] === "string") {
                    let time = Date.parse(r[key]);
                    if (typeof time === "number") {
                        console.log("convertDateColumn map.", key, time);
                        r[key] = new Date(time + 9 * 60 * 60 * 1000);
                    }
                }
            });
            return r;
        });
    }

}

export type SimpleStatistics = {
    count: number;
    legend: string;
    date: Date;
}
export type SearchRequest = {
    limit: number;
    offset?: number;
    status: string;
    media?: string[];
    startDate?: Date;
    endDate?: Date;
}
export type OGP = {
    url: string;
    type: string;
    title: string;
    description: string;
    siteName: string;
    image: string;
}

export type Archive = {
    id: string;
    url: string;
    archivePath: string;
    pageTitle: string;
    saved: Date;
    ogp: OGP;
    extractStatus: string;
    author: string;
}
export type ExtendedArchive = {
    archive: Archive;
    viewerUrl: string;
}

export type Extract = {
    id: string;
    url: string;
    archiveId: string;
    pageTitle: string;
    maker?: string;
    brand?: string;
    item?: string;
    color?: string;
    detectDate?: Date;
    status: string;
    rank: string | undefined | null;
    masterId: string | undefined | null;
    author: string;
}

export type Master = {
    id: string;
    maker: string;
    brandYomi: string;
    brand: string;
    item: string;
    color: string;
    cCode: string;
    cText: string;
    productCategory: string;
    itemCategory: string;
    deleted: boolean;

}

export type Count = {
    count: number;
    label: string;
};

//(String id, JobType type, String createBy, JobStatus status, List<JobParameter> parameter,
// LocalDateTime createdAt, List<JobResult> result, LocalDateTime consumeAt, JobRuntime runtime
export type Job = {
    id: string;
    type: string;
    createBy: string;
    status: string;
    parameter: JobParameter[];
    createdAt: Date;
    result: JobResult[];
    consumeAt: Date;
    runtime: string;
}

export class JobResultParser {
    result: Map<string, string[]> = new Map();
    runId: string = "";
    timestamp: Date = new Date();
    constructor(job: Job) {
        if (job.result != null && job.result.length > 0) {
            this.runId = job.result[0].runId;
            this.timestamp = job.result[0].timestamp;
            job.result.forEach((r) => {
                this.result.set(r.key, r.value);
            });
        }
    }
}

export type JobResult = {
    //String runId, LocalDateTime timestamp, String key, String... value
    runId: string;
    timestamp: Date;
    key: string;
    value: string[];
}
export type JobParameter = {
    key: string;
    value: string[];
}
export type DownloadLink = {
    url: string;
    expire: Date;
};

export enum JobStatus {
    ACCEPTED = "ACCEPTED",
    PROCESSING = "PROCESSING",
    DONE = "DONE",
};



export default CosmeWidgetAPIClient;